import { useEffect, useState } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { Dialog, Spinner } from "@hybrid-iptv/ui";
import { useSettings, type ProviderConfig } from "../settings/settingsStore.js";
import { useMirrorStore } from "./mirrorStore.js";
import { fetchProviderChannels } from "../epg/api.js";

interface MirrorPickerProps {
  open: boolean;
  /** Provider that owns the channel being mirrored. */
  sourceId: string;
  channel: Channel;
  onClose: () => void;
}

/**
 * Section 16: link THIS channel in provider `sourceId` to a fallback channel
 * on a DIFFERENT user-owned provider (TiviMate-style mirror, explicit and
 * user-initiated — never an automatic merge). The picker lists the other
 * configured providers, fetches the chosen one's catalog on demand (one
 * user-initiated fetch, honoring the connection budget), and lets the user
 * search + pick the fallback channel. The mirror store records it; playSource
 * later tokenizes it against the fallback's own provider.
 */
export function MirrorPicker({ open, sourceId, channel, onClose }: MirrorPickerProps) {
  const providers = useSettings((s) => s.providers);
  const hydrated = useMirrorStore((s) => s.hydrated);
  const hydrate = useMirrorStore((s) => s.hydrate);
  const addFallback = useMirrorStore((s) => s.addFallback);
  const fallbacksFor = useMirrorStore((s) => s.fallbacksFor);
  const existing = fallbacksFor(sourceId, channel.id);

  // Other configured providers (not the source of the channel).
  const candidates = providers.filter((p) => p.id !== sourceId);
  const [selectedProvider, setSelectedProvider] = useState<ProviderConfig | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // Load a chosen provider's channel catalog (on demand — one fetch, then keep
  // it for the session so re-picking never re-fetches).
  useEffect(() => {
    if (!selectedProvider || !hydrated || channels.length > 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchProviderChannels(selectedProvider.id, selectedProvider)
      .then((list) => {
        if (!cancelled) setChannels(list);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this provider's channels. Check the source is still valid.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProvider, hydrated, channels.length]);

  const normalized = query.trim().toLowerCase();
  const visible = normalized
    ? channels.filter((c) => c.name.toLowerCase().includes(normalized))
    : channels;

  const alreadyLinked = (providerId: string, targetChannelId: string) =>
    existing.some((f) => f.providerId === providerId && f.channelId === targetChannelId);

  return (
    <Dialog open={open} title={`Fallback for ${channel.name}`} onClose={onClose}>
      <p className="mirror-picker__intro">
        Link this channel to the same show on another provider. If this source's stream fails, the
        player automatically tries the fallback. You can link more than one.
      </p>

      {!hydrated && <Spinner />}
      {/* Step 1: choose the fallback provider. */}
      {hydrated && !selectedProvider && (
        <div className="mirror-picker__providers">
          {candidates.length === 0 && (
            <p className="mirror-picker__empty">
              You need a second source linked in Settings to mirror a channel.
            </p>
          )}
          {candidates.map((provider) => (
            <button
              key={provider.id}
              type="button"
              className="mirror-picker__provider"
              onClick={() => setSelectedProvider(provider)}
            >
              <span>{provider.playlistName || provider.xtreamBaseUrl || provider.m3uUrl || "Unnamed source"}</span>
              <span className="mirror-picker__status">{provider.isValid ? "✓" : "•"}</span>
            </button>
          ))}
        </div>
      )}

      {/* Step 2: pick a channel from the selected provider. */}
      {hydrated && selectedProvider && (
        <>
          <div className="mirror-picker__chosen">
            Fallback source: <strong>{selectedProvider.playlistName || selectedProvider.xtreamBaseUrl || selectedProvider.m3uUrl}</strong>
            <button type="button" onClick={() => { setSelectedProvider(null); setChannels([]); setQuery(""); }}>
              Change
            </button>
          </div>
          <input
            className="mirror-picker__search"
            type="search"
            placeholder={`Search ${selectedProvider.playlistName || "provider"} channels…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search fallback channels"
          />
          <div className="mirror-picker__list">
            {loading && <Spinner />}
            {!loading && error && <p className="mirror-picker__empty">{error}</p>}
            {!loading && !error && visible.length === 0 && (
              <p className="mirror-picker__empty">No channels match.</p>
            )}
            {!loading &&
              !error &&
              visible.slice(0, 60).map((c) => {
                const linked = alreadyLinked(selectedProvider.id, c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`mirror-picker__channel${linked ? " mirror-picker__channel--linked" : ""}`}
                    disabled={linked}
                    onClick={() => {
                      void addFallback(
                        sourceId,
                        channel.id,
                        channel.name,
                        channel.number ?? undefined,
                        {
                          providerId: selectedProvider.id,
                          providerKind: selectedProvider.kind,
                          channelId: c.id,
                          channelName: c.name,
                          streamUrl: c.streamUrl,
                        },
                      );
                    }}
                  >
                    <span>{c.number ? `${c.number} ` : ""}{c.name}</span>
                    <span className="mirror-picker__linked">{linked ? "✓ linked" : "+ link"}</span>
                  </button>
                );
              })}
          </div>
        </>
      )}

      <div className="mirror-picker__linked-summary">
        {hydrated && existing.length === 0 && <span>No fallbacks yet for this channel.</span>}
        {hydrated && existing.length > 0 && (
          <ul>
            {existing.map((f) => (
              <li key={`${f.providerId}:${f.channelId}`}>
                {f.channelName} <em>(linked)</em>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}